import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../axios';
import { permissionDriver } from '../Utils/Constants/utils';

const initialState = {
	Drivers: {
		loading: false,
		driversList: [],
		total: 0,
		allDrivers: []
	},
	SelectedDriver: { loading: false, driver: null },
	PendingDrivers: { loading: false, driversList: [], total: 0 },
};

export const getAllDrivers = createAsyncThunk(
	'Rider/getAllDrivers',
	async (arg, { rejectWithValue }) => {
		const { page = 0, size = 10, search = '', sortBy = 'created', sortDirection = 'DESC' } = arg || {};

		try {
			const params = new URLSearchParams({ page, size, sortBy, sortDirection });
			if (search) params.append('search', encodeURIComponent(search));
			const response = await axios.get(`/drivers?${params}`);

			return {
				drivers: response.data.content,
				total: response.data.totalElements,
			};
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message);
		}
	}
);

export const getFullListDrivers = createAsyncThunk(
	'Driver/getFullListDrivers',
	async () => {

		try {
			const response = await axios.get(`/drivers/all`);
			return response.data;
		} catch (error) {
			const customError = {
				name: 'Custom axios error',
				message: error.response.data.message || error.response.message,
			};
			throw customError;
		}
	}
);

export const addNewDriver = createAsyncThunk('Driver/addNewDriver', async (data) => {
	const Tmpdriver = data.driver;
	const role = permissionDriver;
	const driver = { ...Tmpdriver, role };
	try {
		const response = await axios.post(`drivers`, driver);
		return response.data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
			/* data: error.response.data, */
		};
		throw customError;
	}
});

export const getDriverById = createAsyncThunk('Driver/getDriverById', async (data) => {
	try {
		// Add cache-busting parameter to force fresh data
		const timestamp = new Date().getTime();
		const response = await axios.get(`drivers/${data.driverId}?_t=${timestamp}`);
		return response.data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const deleteDriver = createAsyncThunk('Driver/deleteDriver', async (data) => {
	try {
		await axios.delete(`drivers/${data.driverId}/DRIVER`);
		return data.driverId;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const updateDriver = createAsyncThunk('Driver/updateDriver', async (data) => {
	try {
		const role = permissionDriver;
		const driver = { ...data.driver, role };
		const response = await axios.put(`drivers/${data.driverId}`, driver);
		return response.data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});
export const batchReviewDriverApplications = createAsyncThunk(
	'Driver/batchReviewDriverApplications',
	async ({ driverIds, reviewData }, { rejectWithValue }) => {
		try {
			const response = await axios.put(`drivers/batch/review`, {
				driverIds,
				...reviewData,
			});
			// Return the response data along with the input for state management
			return {
				driverIds,
				reviewData,
				result: response.data,
			};
		} catch (error) {
			const customError = {
				name: 'Custom axios error',
				message: error.response.data.message,
			};
			return rejectWithValue(customError);
		}
	},
);

export const changeDriverStatus = createAsyncThunk('Driver/changeDriverStatus', async (data) => {
	try {
		await axios.put(`users/${data.userId}/status`, { active: data.active });
		return data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const changeDriverAllowBookingDecline = createAsyncThunk(
	'User/changeDriverAllowBookingDecline',
	async (data) => {
		try {
			const response = await axios.patch(
				`users/change-allow-booking-decline/${data.driverId}/${data.status}`,
			);
			return response.data;
		} catch (error) {
			const customError = {
				name: 'Custom axios error',
				message: error.response.data.message,
				/* data: error.response.data, */
			};
			throw customError;
		}
	},
);

export const getPendingDrivers = createAsyncThunk('Driver/getPendingDrivers', async ({
	page = 0, size = 10, sortBy = 'created', sortDirection = 'DESC', search = ''
}) => {
	try {
		const params = new URLSearchParams({ page, size, sortBy, sortDirection });
		if (search) params.append('search', search);
		const response = await axios.get(`drivers/pending?${params}`);
		return {
			pendingDrivers: response.data.content,
			total: response.data.totalElements,
		};
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const reviewDriverApplication = createAsyncThunk(
	'Driver/reviewDriverApplication',
	async ({ driverId, reviewData }, { rejectWithValue }) => {
		try {
			const response = await axios.put(`drivers/${driverId}/review`, reviewData);
			// Return both the response data and the driverId for state management
			return {
				driverId,
				reviewData,
				result: response.data,
			};
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || 'Failed to review driver application';
			return rejectWithValue({
				message: errorMessage,
				driverId,
			});
		}
	},
);
export const driverSlice = createSlice({
	name: 'Driver',
	initialState,
	reducers: {
		resetSelectedDriver: (state) => {
			state.SelectedDriver.driver = null;
		},
		removeDriverFromAllDriversList: (state, action) => {
			const driverId = action.payload;
			state.Drivers.driversList = state.Drivers.driversList.filter(
				(driver) => driver.id !== driverId,
			);
		},
	},
	extraReducers: (builder) => {
		builder
			// getAllDrivers
			.addCase(getAllDrivers.pending, (state) => {
				state.Drivers.loading = true;
			})
			.addCase(getAllDrivers.rejected, (state) => {
				state.Drivers.loading = false;
			})
			.addCase(getAllDrivers.fulfilled, (state, action) => {
				state.Drivers.loading = false;
				state.Drivers.driversList = action.payload.drivers;
				state.Drivers.total = action.payload.total;
			})
			.addCase(getFullListDrivers.pending, (state) => {
				state.Drivers.loading = true;
			})
			.addCase(getFullListDrivers.rejected, (state) => {
				state.Drivers.loading = false;
			})
			.addCase(getFullListDrivers.fulfilled, (state, action) => {
				state.Drivers.loading = false;
				state.Drivers.allDrivers = action.payload;
			})
			// addNewDriver
			.addCase(addNewDriver.pending, (state) => {
				state.PendingDrivers.loading = true;
			})
			.addCase(addNewDriver.rejected, (state) => {
				state.PendingDrivers.loading = false;
			})
			.addCase(addNewDriver.fulfilled, (state, action) => {
				state.PendingDrivers.loading = false;
				// Add new drivers to pending list, not main drivers list
				const newDriver = {
					...action.payload,
					applicationStatus: 'PENDING',
					active: false,
				};
				state.PendingDrivers.driversList.push(newDriver);
			})
			// getDriverById
			.addCase(getDriverById.pending, (state) => {
				state.SelectedDriver.loading = true;
			})
			.addCase(getDriverById.rejected, (state) => {
				state.SelectedDriver.loading = false;
			})
			.addCase(getDriverById.fulfilled, (state, action) => {
				state.SelectedDriver.loading = false;
				state.SelectedDriver.driver = action.payload;
			})
			// deleteDriver
			.addCase(deleteDriver.pending, (state) => {
				state.Drivers.loading = true;
			})
			.addCase(deleteDriver.rejected, (state) => {
				state.Drivers.loading = false;
			})
			.addCase(deleteDriver.fulfilled, (state, action) => {
				state.Drivers.loading = false;
				state.Drivers.driversList = state.Drivers.driversList.filter((item) => {
					return item.id !== action.payload;
				});
			})
			// updateDriver
			.addCase(updateDriver.pending, (state) => {
				state.Drivers.loading = true;
			})
			.addCase(updateDriver.rejected, (state) => {
				state.Drivers.loading = false;
			})
			.addCase(updateDriver.fulfilled, (state, action) => {
				state.Drivers.loading = false;
				state.Drivers.driversList = state.Drivers.driversList.map((item) => {
					if (item.id === action.payload.id) {
						item = action.payload;
						return item;
					}
					return item;
				});
			})
			// changeDriverStatus
			.addCase(changeDriverStatus.pending, (state) => {
				state.Drivers.loading = true;
			})
			.addCase(changeDriverStatus.fulfilled, (state, action) => {
				state.Drivers.loading = false;
				state.Drivers.driversList = state.Drivers.driversList.map((item) => {
					if (item.id === action.payload.userId) {
						item = { ...item, active: action.payload.active };
						return item;
					}
					return item;
				});
			})
			// changeDriverAllowBookingDecline
			.addCase(changeDriverAllowBookingDecline.pending, (state) => {
				state.Drivers.loading = true;
			})
			.addCase(changeDriverAllowBookingDecline.fulfilled, (state, action) => {
				state.Drivers.loading = false;
				state.Drivers.driversList = state.Drivers.driversList.map((item) => {
					if (item.id === action.payload.id) {
						item = {
							...action.payload,
							allowBookingDecline: action.payload.allowBookingDecline,
						};
						return item;
					}
					return item;
				});
			})
			// getPendingDrivers
			.addCase(getPendingDrivers.pending, (state) => {
				state.PendingDrivers.loading = true;
			})
			.addCase(getPendingDrivers.rejected, (state) => {
				state.PendingDrivers.loading = false;
			})
			.addCase(getPendingDrivers.fulfilled, (state, action) => {
				state.PendingDrivers.loading = false;
				state.PendingDrivers.driversList = action.payload.pendingDrivers || [];
				state.PendingDrivers.total = action.payload.total || 0;
			})
			// reviewDriverApplication
			.addCase(reviewDriverApplication.pending, (state) => {
				state.PendingDrivers.loading = true;
			})
			.addCase(reviewDriverApplication.rejected, (state) => {
				state.PendingDrivers.loading = false;
			})
			.addCase(reviewDriverApplication.fulfilled, (state, action) => {
				state.PendingDrivers.loading = false;
				const { driverId, reviewData } = action.payload;
				// Find the driver before removing from pending list
				const driverToReview = state.PendingDrivers.driversList.find(
					(driver) => driver.id === driverId,
				);
				// Remove the reviewed driver from pending list
				state.PendingDrivers.driversList = state.PendingDrivers.driversList.filter(
					(driver) => driver.id !== driverId,
				);
				// If approved, add to main drivers list
				if (reviewData.approved && driverToReview) {
					const updatedDriver = {
						...driverToReview,
						applicationStatus: 'APPROVED',
						active: true,
					};
					state.Drivers.driversList.push(updatedDriver);
				}
			});
	},
});

export default driverSlice.reducer;
export const { resetSelectedDriver, removeDriverFromAllDriversList } = driverSlice.actions;
